"use client";

/**
 * Records a page view per navigation.
 *
 * Collection stopped on 2026-07-31 when hirebee.ai cut over from the old Vite app
 * to this one: the old app wrote page_view_events, this hook was left a no-op
 * stub, and nothing noticed for 17 days. It is called from SiteHeader, which
 * renders on every marketing page.
 *
 * Posts to /api/track rather than inserting directly, so bot classification and
 * country resolution happen server-side where the client cannot influence them
 * (see the route for why that matters).
 *
 * Visitor/session identity comes from src/lib/visitorIdentity.ts, shared with the
 * CTA and conversion trackers — one definition of when a session rolls over.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  getBrowser,
  getDeviceType,
  getSessionId,
  getVisitorId,
  readAndRememberUtm,
  readAndRememberCampaignAttribution,
  readStorage,
  writeStorage,
} from "@/lib/visitorIdentity";

const PREV_PAGE_KEY = "hb_prev_page";

export function usePageViewTracking(): void {
  const pathname = usePathname();
  // React 18 StrictMode double-invokes effects in dev; without this every view
  // would be counted twice locally and the numbers would not be reproducible.
  const lastSent = useRef<string | null>(null);
  const enteredAt = useRef<number>(Date.now());
  const maxScroll = useRef<number>(0);

  useEffect(() => {
    // Not gated on the cookie banner: first-party analytics runs for every visitor
    // (decision 2026-09-25). A gate here also stripped hb_sid from product links, so
    // registrations from visitors who ignored the banner could never be attributed.
    if (typeof window === "undefined" || !pathname) return;
    if (lastSent.current === pathname) return;
    lastSent.current = pathname;

    // Non-throwing: this runs in SiteHeader, and a throw here took the whole header --
    // and every tracker mounted in it -- down where storage is blocked.
    const previousPage = readStorage("session", PREV_PAGE_KEY);
    writeStorage("session", PREV_PAGE_KEY, pathname);

    // Time/scroll belong to the page being LEFT, so they are reported for the
    // previous view on the next navigation rather than guessed at entry.
    const timeOnPrev = Date.now() - enteredAt.current;
    const scrollOnPrev = maxScroll.current;
    enteredAt.current = Date.now();
    maxScroll.current = 0;

    // First-touch: the campaign params arrive on the landing view and are gone
    // from the URL by the second page, so they are remembered for the session and
    // stamped on every view in it.
    const utm = readAndRememberUtm();
    // Kept for the visit so a booking several pages later is still credited.
    readAndRememberCampaignAttribution();

    const payload = {
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      ...utm,
      page_path: pathname,
      page_title: document.title,
      referrer: document.referrer || null,
      previous_page: previousPage,
      time_on_page_ms: previousPage ? timeOnPrev : null,
      max_scroll_depth: previousPage ? scrollOnPrev : null,
      device_type: getDeviceType(),
      browser: getBrowser(),
    };

    // keepalive so the request survives the navigation that triggered it.
    fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* never let analytics break a page render */
    });
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.round((window.scrollY / scrollable) * 100);
      if (pct > maxScroll.current) maxScroll.current = Math.min(pct, 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
}
